// /admin/templates · admin-only project template manager.
// V1: read-only listing + instantiate button per template.
// Edit/create UI for templates is deferred (templates live in SQL seed for now).

import { useState, useEffect } from 'react';
import * as api from '../lib/api.ts';
import { Icon } from '../components/Icon.jsx';
import { navigate } from '../lib/routing.js';
import { TemplateEditModal } from '../components/TemplateEditModal.jsx';
import { isElevated } from '../lib/auth.ts';
import { confirmDialog } from '../lib/confirm.js';

export const AdminTemplates = ({ user, staff, groups }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [instantiateTmpl, setInstantiateTmpl] = useState(null);
  const [editTmpl, setEditTmpl] = useState(null);
  const [showNewTmpl, setShowNewTmpl] = useState(false);

  async function refresh() {
    try {
      const list = await api.listProjectTemplates();
      setTemplates(list);
      setLoading(false);
    } catch (e) {
      setErr(e.message || '加载失败');
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function archive(t) {
    if (!await confirmDialog({ title: '归档模板', confirmLabel: '归档', message: `确认归档模板 "${t.name}"？现有的待办实例不受影响。` })) return;
    await api.archiveTemplate(t.id);
    await refresh();
  }

  if (!isElevated(user)) {
    return <div className="vava-card empty-state"><div>仅管理员 / 店长</div></div>;
  }
  if (loading) return <div className="vava-card empty-state">加载中…</div>;
  if (err) return <div className="vava-card empty-state"><div>{err}</div></div>;

  function ownerName(id) {
    return staff.find(s => s.id === id)?.name || id;
  }

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">项目模板</h1>
          <div className="vava-page-meta">
            可重复执行的项目结构（新品上新, 招聘流程等）· 点击「从模板新建」生成完整的待办 + 子项
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewTmpl(true)}>
          <Icon name="plus"/> 新建模板
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="vava-card empty-state">
          <div className="emoji">📋</div>
          <div className="title">还没有模板</div>
          <div className="sub">在 seeds/ 添加 project_templates 记录</div>
        </div>
      ) : (
        templates.map(t => (
          <section key={t.id} className="tmpl-card">
            <header className="tmpl-card-head">
              <div>
                <h2 className="tmpl-card-name">{t.name}</h2>
                {t.description && <div className="tmpl-card-desc">{t.description}</div>}
                <div className="tmpl-card-meta mono">
                  {t.subtasks.length} 个子项模板 · 范围 {Math.min(...t.subtasks.map(s => s.offsetDays))} 至 +{Math.max(...t.subtasks.map(s => s.offsetDays))} 天
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <button className="raci-edit-btn" onClick={() => setEditTmpl(t)}>编辑</button>
                <button className="raci-edit-btn" style={{ color: 'var(--vava-red)' }}
                        onClick={() => archive(t)}>归档</button>
                <button className="btn btn-primary" onClick={() => setInstantiateTmpl(t)}>
                  <Icon name="plus"/> 从模板新建待办
                </button>
              </div>
            </header>
            <table className="tmpl-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th style={{ width: 72 }} className="num">偏移</th>
                  <th>子项标题</th>
                  <th style={{ width: 80 }}>优先级</th>
                  <th>负责人</th>
                </tr>
              </thead>
              <tbody>
                {t.subtasks.map(s => (
                  <tr key={s.ord}>
                    <td className="mono">{s.ord}</td>
                    <td className="mono num" style={{ color: s.offsetDays < 0 ? 'var(--vava-amber)' : 'var(--vava-text-muted)' }}>
                      {s.offsetDays > 0 ? '+' : ''}{s.offsetDays}d
                    </td>
                    <td>{s.title}</td>
                    <td><span className={`prio-pill prio-${s.priority}`}>{s.priority}</span></td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {s.owners.map(o => `${o.role}: ${ownerName(o.staffId)}`).join('  ·  ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))
      )}

      {instantiateTmpl && (
        <InstantiateModal template={instantiateTmpl}
                          onClose={() => setInstantiateTmpl(null)}
                          onCreated={(task) => {
                            setInstantiateTmpl(null);
                            navigate('/task/' + task.id);
                          }} />
      )}
      {(showNewTmpl || editTmpl) && (
        <TemplateEditModal template={editTmpl}
                           groups={groups}
                           staff={staff}
                           onClose={() => { setShowNewTmpl(false); setEditTmpl(null); }}
                           onSaved={refresh} />
      )}
    </>
  );
};

export const InstantiateModal = ({ template, onClose, onCreated }) => {
  const [title, setTitle] = useState(template.name + ' · ');
  const [anchor, setAnchor] = useState(() => {
    // Default = today + 30 days
    const d = new Date(Date.now() + 30 * 86_400_000);
    return d.toISOString().slice(0, 10);
  });
  const [priority, setPriority] = useState('P1');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function save() {
    if (!title.trim() || !anchor) {
      setErr('标题和锚定日期必填');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const task = await api.instantiateTemplate(template.id, {
        title: title.trim(),
        anchor_date: anchor,
        description: description.trim() || undefined,
        priority
      });
      onCreated?.(task);
    } catch (e) {
      setErr(e.message || '创建失败');
    } finally {
      setSaving(false);
    }
  }

  // Preview computed dates
  const anchorDate = new Date(anchor + 'T00:00:00Z');
  const previewSubtasks = template.subtasks.map(s => {
    const d = new Date(anchorDate.getTime() + s.offsetDays * 86_400_000);
    return { ...s, dueDate: d.toISOString().slice(0, 10) };
  });

  return (
    <div className="vava-modal-backdrop" onClick={onClose}>
      <div className="vava-modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="vava-modal-head">
          <h2>从模板新建：{template.name}</h2>
          <button className="vava-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="vava-modal-body">
          <div className="vava-field">
            <label className="vava-label">待办标题 *</label>
            <input className="vava-input" value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="如：新品上新 · 2027 年 5 月" autoFocus />
          </div>
          <div className="vava-field">
            <label className="vava-label">锚定日期（finalize 日期）*</label>
            <input className="vava-input" type="date" value={anchor}
                   onChange={(e) => setAnchor(e.target.value)} />
            <div style={{ fontSize: 11, color: 'var(--vava-text-subtle)', marginTop: 4 }}>
              子项日期会基于锚定日期 ± 偏移天数自动计算
            </div>
          </div>
          <div className="vava-field">
            <label className="vava-label">优先级</label>
            <select className="vava-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="P0">P0 紧急</option>
              <option value="P1">P1</option>
              <option value="P2">P2</option>
            </select>
          </div>
          <div className="vava-field">
            <label className="vava-label">备注（可选）</label>
            <textarea className="vava-textarea" rows={2}
                      value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="vava-field">
            <label className="vava-label">预览生成日期</label>
            <ul className="tmpl-preview-list">
              {previewSubtasks.map(s => (
                <li key={s.ord}>
                  <span className="mono">{s.dueDate}</span>
                  <span className="mono" style={{ color: 'var(--vava-text-subtle)', marginLeft: 8 }}>
                    ({s.offsetDays > 0 ? '+' : ''}{s.offsetDays}d)
                  </span>
                  <span style={{ marginLeft: 12 }}>{s.title}</span>
                </li>
              ))}
            </ul>
          </div>

          {err && <div className="att-error" role="alert">{err}</div>}
        </div>
        <div className="vava-modal-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>取消</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? '创建中…' : `创建 (1 待办 + ${template.subtasks.length} 子项)`}
          </button>
        </div>
      </div>
    </div>
  );
};
